<template>
  <main class="mt-3">
    <loading-spinner :visible="isLoading" />

    <div class="container-fluid eis-dashboard">

      <!-- 헤더 -->
      <header class="eis-header">
        <div class="eis-eyebrow">Executive Information System</div>
        <h2 class="eis-title">EIS 대시보드</h2>
        <p class="eis-subtitle">일반예약 · 호텔예약 · 외부결제 통합 매출 현황</p>
      </header>

      <!-- 조회기간 -->
      <section class="filter-card">
        <div class="filter-dates">
          <label class="date-field">
            <span>시작일</span>
            <input type="date" v-model="dateStart" ref="dateStart" @change="activeRange = 'custom'">
          </label>
          <span class="date-sep">~</span>
          <label class="date-field">
            <span>종료일</span>
            <input type="date" v-model="dateEnd" ref="dateEnd" @change="activeRange = 'custom'">
          </label>
        </div>

        <div class="filter-actions">
          <div class="range-pills">
            <button type="button" class="pill" :class="{ active: activeRange === '30' }" @click="onQuickRange(30)">최근 30일</button>
            <button type="button" class="pill" :class="{ active: activeRange === 'month' }" @click="onQuickRange('month')">이번달</button>
            <button type="button" class="pill" :class="{ active: activeRange === 'year' }" @click="onQuickRange('year')">올해</button>
          </div>
          <div class="range-pills compare-pills">
            <button type="button" class="pill" :class="{ active: compareMode === 'yoy' }" @click="onCompareMode('yoy')">전년 동기 비교</button>
            <button type="button" class="pill" :class="{ active: compareMode === 'prev' }" @click="onCompareMode('prev')">직전 기간 비교</button>
          </div>
          <button type="button" class="btn-search" @click="loadDashboard">
            <i class="bi bi-search"></i>&nbsp;조회
          </button>
        </div>
      </section>

      <p class="eis-note">
        <i class="bi bi-info-circle"></i>
        집계 기준 : 일반예약/호텔예약은 확정일 기준, 외부결제는 결제일 기준입니다.
        <br>비교 기간 : {{ compareStart.replace(/-/g, '.') }} ~ {{ compareEnd.replace(/-/g, '.') }} ({{ compareLabel }})
      </p>

      <!-- 핵심 지표 -->
      <section class="hero-card" v-if="kpiCards.length">
        <div class="hero-label">{{ kpiCards[0].label }}</div>
        <div class="hero-value">{{ kpiCards[0].value }}</div>
        <div class="hero-delta" :class="'tone-' + kpiCards[0].delta.tone">
          {{ kpiCards[0].delta.text }}<span>{{ compareLabel }} 대비 (비교값 {{ formatCompact(prevCombinedTotalAmt) }}원)</span>
        </div>
        <div class="hero-sub">{{ kpiCards[0].sub }}</div>

        <div class="hero-stats">
          <div class="hero-stat" v-for="(k, i) in kpiCards.slice(1)" :key="i">
            <div class="hero-stat-label">{{ k.label }}</div>
            <div class="hero-stat-value" :class="{ 'is-alert': k.alert }">{{ k.value }}</div>
            <div class="hero-stat-delta" v-if="k.delta" :class="'tone-' + k.delta.tone">{{ k.delta.text }}</div>
            <div class="hero-stat-sub">{{ k.sub }}</div>
          </div>
        </div>
      </section>

      <!-- 채널별 현황 (신규 컨텐츠는 eis-section 단위로 추가) -->
      <section class="eis-section">
        <h3 class="section-title">채널별 현황</h3>
        <div class="channel-scroll">
          <div class="channel-card" v-for="(c, key) in channelCards" :key="key">
            <div class="channel-head">
              <span class="channel-name">
                <span class="channel-dot" :style="{ backgroundColor: c.color }"></span>{{ c.title }}
              </span>
              <span class="channel-share">{{ getShare(c) }}%</span>
            </div>

            <div class="channel-amt">{{ getCurrencyFormat(c.totalAmt) }}<small>원</small></div>
            <div class="channel-delta" :class="'tone-' + getChannelDelta(c).tone">
              {{ getChannelDelta(c).text }}<span>{{ compareLabel }} 대비</span>
            </div>

            <div class="share-track">
              <div class="share-fill" :style="{ width: getShare(c) + '%', backgroundColor: c.color }"></div>
            </div>

            <div class="channel-rows">
              <div class="channel-row">
                <span>확정건수</span>
                <b>{{ getCurrencyFormat(c.totalCnt) }}건</b>
              </div>
              <div class="channel-row">
                <span>취소건수</span>
                <b :class="{ 'is-alert': c.cxCnt > 0 }">{{ getCurrencyFormat(c.cxCnt) }}건</b>
              </div>
              <div class="channel-row">
                <span>취소율</span>
                <b :class="{ 'is-alert': getCxRate(c) > 10 }">{{ getCxRate(c) }}%</b>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 상세 분석 -->
      <section class="eis-section">
        <h3 class="section-title">매출 분석</h3>

        <div class="row g-3 mb-3">
          <div class="col-lg-8">
            <div class="chart-card">
              <div class="chart-card-header">통합 매출 추이</div>
              <div class="chart-canvas-wrap">
                <canvas ref="trendChart"></canvas>
              </div>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="chart-card">
              <div class="chart-card-header">채널별 매출 비중</div>
              <div class="chart-canvas-wrap">
                <canvas ref="shareChart"></canvas>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-lg-7">
            <div class="chart-card">
              <div class="chart-card-header">채널별 확정/취소 건수</div>
              <div class="chart-canvas-wrap">
                <canvas ref="statusChart"></canvas>
              </div>
            </div>
          </div>
          <div class="col-lg-5">
            <div class="chart-card">
              <div class="chart-card-header">채널별 취소율 비교</div>
              <div class="chart-canvas-wrap">
                <canvas ref="cxRateChart"></canvas>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 상품 순위 -->
      <section class="eis-section">
        <h3 class="section-title">상품 순위 TOP 5<small class="section-sub">일반예약 확정 기준</small></h3>

        <div class="row g-3">
          <div class="col-lg-6" v-for="r in rankBoards" :key="r.key">
            <div class="rank-card">
              <div class="rank-card-header">{{ r.title }}</div>
              <div class="rank-empty" v-if="!r.list.length">조회기간 내 확정된 판매 내역이 없습니다.</div>
              <div class="rank-row" v-for="(p, i) in r.list" :key="p.pdtCode">
                <span class="rank-no" :class="{ 'is-top': i < 3 }">{{ i + 1 }}</span>
                <div class="rank-body">
                  <div class="rank-name" :title="p.pdtName">{{ p.pdtName }}</div>
                  <div class="rank-track">
                    <div class="rank-fill" :style="{ width: getRankRatio(r, p) + '%' }"></div>
                  </div>
                </div>
                <div class="rank-figures">
                  <b>{{ r.key === 'cnt' ? getCurrencyFormat(p.cnt) + '건' : formatCompact(p.amt) + '원' }}</b>
                  <span>{{ r.key === 'cnt' ? formatCompact(p.amt) + '원' : getCurrencyFormat(p.cnt) + '건' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="eis-footer">
        <button type="button" class="btn-back" @click="goToBack()">돌아가기</button>
      </div>

    </div>
  </main>
</template>

<script>
import Chart from 'chart.js/auto'
import LoadingSpinner from '../components/LoadingSpinner.vue';     // 로딩스피너 컴포넌트

//채널별 시그니처 컬러(카드 강조/차트 색상에서 동일하게 재사용)
const CH_COLOR = {
  reservation: '#4C6EF5',   //일반예약
  hotel:       '#12B886',   //호텔예약
  external:    '#F59F00',   //외부결제
}
const CX_COLOR = '#E5484D'  //취소(공통)
const INK = '#1B2434'       //기본 문자색
const MUTED = '#8A93A3'     //보조 문자색
const GRID = '#EEF0F3'      //차트 격자선
const FONT = "'Pretendard', 'Noto Sans KR', 'Malgun Gothic', sans-serif"

//chart.js 공통 기본값
Chart.defaults.font.family = FONT
Chart.defaults.font.size = 11
Chart.defaults.color = MUTED

//차트 공통 범례/툴팁 스타일
const LEGEND_BOTTOM = {
  position: 'bottom',
  labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, padding: 14 }
}
const TOOLTIP_BASE = {
  backgroundColor: INK,
  padding: 10,
  cornerRadius: 8,
  titleFont: { weight: '600' },
  boxPadding: 4,
}

export default {
  components: {
    LoadingSpinner
  },
  data() {
    return {
      isLoading: false,

      dateStart: "",           //조회시작일
      dateEnd: "",             //조회종료일
      activeRange: "",         //빠른기간 선택상태(30 / month / year / custom)

      compareMode: "yoy",      //비교기준(yoy 전년동기 / prev 직전기간)
      compareStart: "",        //비교시작일
      compareEnd: "",          //비교종료일

      //채널별 요약데이타(백엔드 미연결시에도 화면이 비지 않도록 기본구조 유지)
      reservationSummary: { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },
      hotelSummary:       { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },
      externalSummary:    { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },

      //비교기간 채널별 요약데이타
      prevReservationSummary: { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },
      prevHotelSummary:       { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },
      prevExternalSummary:    { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },

      //상품별 판매집계(일반예약 확정건, 상품순위 TOP5 산출용)
      productSales: [],

      //chart.js 인스턴스 보관(재조회 시 destroy 후 재생성)
      charts: {
        trend: null,
        share: null,
        status: null,
        cxRate: null,
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },

    combinedTotalAmt() {
      return this.reservationSummary.totalAmt + this.hotelSummary.totalAmt + this.externalSummary.totalAmt
    },
    combinedTotalCnt() {
      return this.reservationSummary.totalCnt + this.hotelSummary.totalCnt + this.externalSummary.totalCnt
    },
    combinedCxCnt() {
      return this.reservationSummary.cxCnt + this.hotelSummary.cxCnt + this.externalSummary.cxCnt
    },
    combinedCxRate() {
      let base = this.combinedTotalCnt + this.combinedCxCnt
      return base > 0 ? ((this.combinedCxCnt / base) * 100).toFixed(1) : "0.0"
    },
    topChannel() {
      let arr = [
        { name: "일반예약", amt: this.reservationSummary.totalAmt },
        { name: "호텔예약", amt: this.hotelSummary.totalAmt },
        { name: "외부결제", amt: this.externalSummary.totalAmt },
      ]
      arr.sort((a, b) => b.amt - a.amt)
      return arr[0]
    },

    //비교기준 표시명
    compareLabel() {
      return this.compareMode === 'prev' ? '직전 기간' : '전년 동기'
    },
    prevCombinedTotalAmt() {
      return this.prevReservationSummary.totalAmt + this.prevHotelSummary.totalAmt + this.prevExternalSummary.totalAmt
    },
    prevCombinedTotalCnt() {
      return this.prevReservationSummary.totalCnt + this.prevHotelSummary.totalCnt + this.prevExternalSummary.totalCnt
    },
    prevCombinedCxCnt() {
      return this.prevReservationSummary.cxCnt + this.prevHotelSummary.cxCnt + this.prevExternalSummary.cxCnt
    },
    prevCombinedCxRate() {
      let base = this.prevCombinedTotalCnt + this.prevCombinedCxCnt
      return base > 0 ? ((this.prevCombinedCxCnt / base) * 100).toFixed(1) : "0.0"
    },

    //첫번째 항목은 대표지표(히어로 카드), 나머지는 하단 보조지표로 표시됨
    kpiCards() {
      return [
        {
          label: "통합 총매출액",
          value: this.getCurrencyFormat(this.combinedTotalAmt) + "원",
          sub: `${this.dateStart.replace(/-/g, '.')} ~ ${this.dateEnd.replace(/-/g, '.')}`,
          delta: this.getDeltaInfo(this.combinedTotalAmt, this.prevCombinedTotalAmt),
        },
        {
          label: "확정건수",
          value: this.getCurrencyFormat(this.combinedTotalCnt) + "건",
          sub: "확정 기준",
          delta: this.getDeltaInfo(this.combinedTotalCnt, this.prevCombinedTotalCnt),
        },
        {
          label: "취소율",
          value: this.combinedCxRate + "%",
          sub: `취소 ${this.getCurrencyFormat(this.combinedCxCnt)}건`,
          alert: Number(this.combinedCxRate) > 10,
          delta: this.getDeltaInfo(this.combinedCxRate, this.prevCombinedCxRate, 'point', true),
        },
        {
          label: "최고매출",
          value: this.topChannel.name,
          sub: this.formatCompact(this.topChannel.amt) + "원",
        },
      ]
    },

    channelCards() {
      return {
        reservation: { title: "일반예약", color: CH_COLOR.reservation, ...this.reservationSummary, prev: this.prevReservationSummary },
        hotel:       { title: "호텔예약", color: CH_COLOR.hotel, ...this.hotelSummary, prev: this.prevHotelSummary },
        external:    { title: "외부결제", color: CH_COLOR.external, ...this.externalSummary, prev: this.prevExternalSummary },
      }
    },

    //최다판매 TOP5(건수 기준, 동률이면 매출액 순)
    topByCnt() {
      return [...this.productSales]
        .sort((a, b) => (b.cnt - a.cnt) || (b.amt - a.amt))
        .slice(0, 5)
    },

    //최다매출 TOP5(매출액 기준, 동률이면 건수 순)
    topByAmt() {
      return [...this.productSales]
        .sort((a, b) => (b.amt - a.amt) || (b.cnt - a.cnt))
        .slice(0, 5)
    },

    //상품순위 카드 구성(key는 막대길이 기준값)
    rankBoards() {
      return [
        { key: 'cnt', title: '최다판매 상품', list: this.topByCnt },
        { key: 'amt', title: '최다매출 상품', list: this.topByAmt },
      ]
    },
  },
  created() {
    //기본조회기간: 당해년 1/1 ~ 오늘
    this.setQuickRange('year')
  },
  mounted() {
    if(this.user.handlerGrade > 4 || this.user.handlerGrade == undefined) {
      var swal = require('sweetalert2');
      swal.fire({
        title: '',
        text: '로그인 권한이 없습니다.',
        icon: 'error'
      });
      this.$router.push({path: '/b2bLogin'});
      return;     //이후 로직(loadDashboard 등) 실행 방지
    }

    document.getElementById("user").style.display = "none";

    this.loadDashboard();
  },
  beforeUnmount() {
    //차트인스턴스 정리(메모리누수 방지)
    Object.keys(this.charts).forEach(key => {
      if(this.charts[key]) {
        this.charts[key].destroy()
        this.charts[key] = null
      }
    })
  },
  methods:
  {
    //mixins 공통함수
    getDateFormat(date)
    {
      return this.$dateFormat(date);
    },
    getCurrencyFormat(value)
    {
      return this.$currencyFormat(value);
    },

    //금액 축약표기(예: 3.2억, 4,500만) - 모바일 차트축/보조표기용
    formatCompact(value)
    {
      let v = Number(value) || 0
      let abs = Math.abs(v)

      if(abs >= 100000000)
      {
        return (v / 100000000).toFixed(1).replace(/\.0$/, '') + '억'
      }
      if(abs >= 10000)
      {
        return this.getCurrencyFormat(Math.round(v / 10000)) + '만'
      }
      return this.getCurrencyFormat(v)
    },

    //취소율(%) 계산(공통)
    getCxRate(summary)
    {
      let base = summary.totalCnt + summary.cxCnt
      return base > 0 ? ((summary.cxCnt / base) * 100).toFixed(1) : "0.0"
    },

    //채널별 매출비중(%) 계산
    getShare(summary)
    {
      return this.combinedTotalAmt > 0 ? ((summary.totalAmt / this.combinedTotalAmt) * 100).toFixed(1) : "0.0"
    },

    //증감 표시정보 계산
    //type : 'rate' 증감률(%), 'point' 퍼센트포인트 차이(%p) / inverse : true면 감소가 좋은 지표(취소율 등)
    getDeltaInfo(cur, prev, type = 'rate', inverse = false)
    {
      cur = Number(cur) || 0
      prev = Number(prev) || 0

      let diff = 0
      let text = ''

      if(type === 'point')
      {
        diff = cur - prev
        text = Math.abs(diff).toFixed(1) + '%p'
      }
      else
      {
        if(prev === 0) return { text: cur > 0 ? '비교값 없음' : '-', tone: 'na' }
        diff = ((cur - prev) / prev) * 100
        text = Math.abs(diff).toFixed(1) + '%'
      }

      if(Math.abs(diff) < 0.05) return { text: '변동 없음', tone: 'flat' }

      let up = diff > 0
      let good = inverse ? !up : up
      return { text: (up ? '▲ ' : '▼ ') + text, tone: good ? 'good' : 'bad' }
    },

    //상품순위 막대길이(해당 순위표 1위 대비 %)
    getRankRatio(board, p)
    {
      let max = board.list.length ? Number(board.list[0][board.key]) || 0 : 0
      return max > 0 ? ((Number(p[board.key]) / max) * 100).toFixed(1) : 0
    },

    //상품별 판매집계 응답을 숫자형으로 정리
    buildProductSales(rows)
    {
      if(!Array.isArray(rows)) return []

      return rows.map(r => ({
        pdtCode: r.pdtCode,
        pdtName: r.pdtName || r.pdtCode,
        cnt:     Number(r.cnt) || 0,
        amt:     Number(r.amt) || 0,
      }))
    },

    //채널카드 매출 증감
    getChannelDelta(c)
    {
      return this.getDeltaInfo(c.totalAmt, c.prev.totalAmt)
    },

    //문자열(yyyy-mm-dd 또는 yyyy.mm.dd)에서 숫자만 뽑아 Date 생성(연-월-일 순서, 4자리 연도 전제)
    parseDate(str)
    {
      let p = String(str).match(/\d+/g)
      return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]))
    },

    //두 날짜 사이의 일수 차이
    dayDiff(from, to)
    {
      return Math.round((this.parseDate(to) - this.parseDate(from)) / 86400000)
    },

    //연도 이동(윤년 2/29 는 2/28 로 보정)
    shiftYear(str, diff)
    {
      let d = this.parseDate(str)
      let t = new Date(d.getFullYear() + diff, d.getMonth(), d.getDate())
      if(t.getMonth() !== d.getMonth()) t = new Date(d.getFullYear() + diff, d.getMonth() + 1, 0)
      return this.toDateInput(t)
    },

    //비교기간 계산
    getCompareRange()
    {
      if(this.compareMode === 'prev')
      {
        let s = this.parseDate(this.dateStart)
        let days = this.dayDiff(this.dateStart, this.dateEnd) + 1
        let pe = new Date(s)
        pe.setDate(pe.getDate() - 1)
        let ps = new Date(pe)
        ps.setDate(ps.getDate() - (days - 1))
        return { start: this.toDateInput(ps), end: this.toDateInput(pe) }
      }
      return { start: this.shiftYear(this.dateStart, -1), end: this.shiftYear(this.dateEnd, -1) }
    },

    //비교기준 버튼 클릭 : 기준변경 후 즉시 조회
    onCompareMode(mode)
    {
      this.compareMode = mode
      this.loadDashboard()
    },

    //빠른기간 버튼 클릭 : 기간설정 후 즉시 조회
    onQuickRange(param)
    {
      this.setQuickRange(param)
      this.loadDashboard()
    },

    //빠른기간설정
    setQuickRange(param)
    {
      let today = new Date();
      let start = new Date();

      if(param === "month")
      {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
      }
      else if(param === "year")
      {
        start = new Date(today.getFullYear(), 0, 1)   // 당해년 1월 1일
      }
      else
      {
        start.setDate(today.getDate() - (param - 1))
      }

      this.activeRange = String(param)
      this.dateEnd = this.toDateInput(today)
      this.dateStart = this.toDateInput(start)
    },

    //Date -> yyyy-mm-dd
    toDateInput(d)
    {
      let yyyy = d.getFullYear();
      let mm = String(d.getMonth() + 1).padStart(2, '0');
      let dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    //대시보드 전체 데이타 로드(3채널 x 현재기간/비교기간 병렬조회)
    async loadDashboard()
    {
      if(!this.dateStart || !this.dateEnd)
      {
        this.$swal.fire("","조회기간을 입력해 주십시오.","warning")
        return false;
      }
      if(this.dateStart > this.dateEnd)
      {
        this.$swal.fire("","시작일은 종료일보다 늦을 수 없습니다.","warning")
        return false;
      }

      this.isLoading = true;

      let range = this.getCompareRange()
      this.compareStart = range.start
      this.compareEnd = range.end

      try
      {
        //현재기간 3건 + 비교기간 3건(동일 alias, 날짜 파라미터만 다름) + 상품별 판매집계 1건. 응답 스펙은 하단 buildSummary 주석 참고
        let [resSum, hotelSum, extSum, prevResSum, prevHotelSum, prevExtSum, pdtSales] = await Promise.all([
          this.$api("/api/getEisReservationSummary", { param: [this.dateStart, this.dateEnd] }),
          this.$api("/api/getEisHotelSummary",       { param: [this.dateStart, this.dateEnd] }),
          this.$api("/api/getEisExternalPaySummary", { param: [this.dateStart, this.dateEnd] }),
          this.$api("/api/getEisReservationSummary", { param: [range.start, range.end] }),
          this.$api("/api/getEisHotelSummary",       { param: [range.start, range.end] }),
          this.$api("/api/getEisExternalPaySummary", { param: [range.start, range.end] }),
          this.$api("/api/getEisProductSales",       { param: [this.dateStart, this.dateEnd] }),
        ])

        this.reservationSummary = this.buildSummary(resSum)
        this.hotelSummary       = this.buildSummary(hotelSum)
        this.externalSummary    = this.buildSummary(extSum)

        this.prevReservationSummary = this.buildSummary(prevResSum)
        this.prevHotelSummary       = this.buildSummary(prevHotelSum)
        this.prevExternalSummary    = this.buildSummary(prevExtSum)

        this.productSales = this.buildProductSales(pdtSales)

        this.$nextTick(() => {
          this.renderCharts()
        })
      }
      catch(err)
      {
        console.error("loadDashboard error:", err)
        this.$swal.fire("","대시보드 데이타 조회 중 오류가 발생했습니다.","error")
      }
      finally
      {
        this.isLoading = false;
      }
    },

    // API 응답(일자별 집계행 배열)을 화면에서 쓰는 요약구조로 변환
    // 기대하는 응답형태: [ { eisDate:'2026-07-01', amt:450000, cnt:3, cxAmt:0, cxCnt:0 }, ... ]
    buildSummary(rows)
    {
      let base = { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] }
      if(!Array.isArray(rows)) return base

      rows.forEach(r => {
        base.totalAmt += Number(r.amt) || 0
        base.totalCnt += Number(r.cnt) || 0
        base.cxAmt    += Number(r.cxAmt) || 0
        base.cxCnt    += Number(r.cxCnt) || 0
        base.trend.push({ date: this.getDateFormat(r.eisDate), amt: Number(r.amt) || 0 })
      })
      return base
    },

    //라인차트 영역 그라데이션(위쪽 연하게 채우고 아래로 투명)
    makeGradient(context, color)
    {
      let chart = context.chart
      if(!chart.chartArea) return color + '22'

      let g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom)
      g.addColorStop(0, color + '33')
      g.addColorStop(1, color + '00')
      return g
    },

    //차트전체 재렌더링
    renderCharts()
    {
      this.renderTrendChart()
      this.renderShareChart()
      this.renderStatusChart()
      this.renderCxRateChart()
    },

    //1) 통합 매출 추이(라인차트, 채널별 3계열 + 통합합계 + 비교기간 합계)
    renderTrendChart()
    {
      if(this.charts.trend) this.charts.trend.destroy()

      //3채널의 날짜를 합쳐서 x축 라벨 구성
      let dateSet = new Set()
      ;[this.reservationSummary, this.hotelSummary, this.externalSummary].forEach(s => {
        s.trend.forEach(t => dateSet.add(t.date))
      })
      let labels = Array.from(dateSet).sort()

      let toSeries = (summary) => labels.map(d => {
        let found = summary.trend.find(t => t.date === d)
        return found ? found.amt : 0
      })

      //비교기간 합계를 "시작일로부터 n일째" 기준으로 맞춰 현재기간 x축에 겹침
      let prevByOffset = {}
      ;[this.prevReservationSummary, this.prevHotelSummary, this.prevExternalSummary].forEach(s => {
        s.trend.forEach(t => {
          let off = this.dayDiff(this.compareStart, t.date)
          prevByOffset[off] = (prevByOffset[off] || 0) + t.amt
        })
      })
      let prevSeries = labels.map(d => prevByOffset[this.dayDiff(this.dateStart, d)] || 0)

      let curSeries = [this.reservationSummary, this.hotelSummary, this.externalSummary].map(toSeries)
      let totalSeries = labels.map((d, i) => curSeries[0][i] + curSeries[1][i] + curSeries[2][i])

      let makeDataset = (label, summary, color) => ({
        label: label,
        data: toSeries(summary),
        borderColor: color,
        backgroundColor: (context) => this.makeGradient(context, color),
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4,
      })

      let ctx = this.$refs.trendChart.getContext('2d')
      this.charts.trend = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            makeDataset('일반예약', this.reservationSummary, CH_COLOR.reservation),
            makeDataset('호텔예약', this.hotelSummary, CH_COLOR.hotel),
            makeDataset('외부결제', this.externalSummary, CH_COLOR.external),
            {
              label: '통합 합계',
              data: totalSeries,
              borderColor: INK,
              backgroundColor: 'transparent',
              borderWidth: 2,
              tension: 0.35,
              fill: false,
              pointRadius: 0,
              pointHoverRadius: 4,
            },
            {
              label: this.compareLabel + ' 합계',
              data: prevSeries,
              borderColor: '#94A3B8',
              backgroundColor: 'transparent',
              borderWidth: 2,
              borderDash: [6, 4],
              tension: 0.35,
              fill: false,
              pointRadius: 0,
              pointHoverRadius: 4,
            },
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          scales: {
            x: {
              grid: { display: false },
              ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6 }
            },
            y: {
              border: { display: false },
              grid: { color: GRID },
              ticks: {
                maxTicksLimit: 5,
                callback: (v) => this.formatCompact(v)
              }
            }
          },
          plugins: {
            legend: LEGEND_BOTTOM,
            tooltip: {
              ...TOOLTIP_BASE,
              callbacks: {
                label: (c) => `${c.dataset.label}: ${this.getCurrencyFormat(c.raw)}원`
              }
            }
          }
        }
      })
    },

    //2) 채널별 매출비중(도넛차트, 중앙에 통합매출 표기)
    renderShareChart()
    {
      if(this.charts.share) this.charts.share.destroy()

      let self = this
      let ctx = this.$refs.shareChart.getContext('2d')
      this.charts.share = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['일반예약', '호텔예약', '외부결제'],
          datasets: [{
            data: [this.reservationSummary.totalAmt, this.hotelSummary.totalAmt, this.externalSummary.totalAmt],
            backgroundColor: [CH_COLOR.reservation, CH_COLOR.hotel, CH_COLOR.external],
            borderWidth: 3,
            borderColor: '#ffffff',
            hoverOffset: 4,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: {
            legend: LEGEND_BOTTOM,
            tooltip: {
              ...TOOLTIP_BASE,
              callbacks: {
                label: (c) => {
                  let total = c.dataset.data.reduce((a, b) => a + b, 0)
                  let pct = total > 0 ? ((c.raw / total) * 100).toFixed(1) : 0
                  return `${c.label}: ${self.getCurrencyFormat(c.raw)}원 (${pct}%)`
                }
              }
            }
          }
        },
        plugins: [{
          id: 'centerText',
          afterDraw(chart) {
            let area = chart.chartArea
            if(!area) return
            let x = (area.left + area.right) / 2
            let y = (area.top + area.bottom) / 2
            let c = chart.ctx

            c.save()
            c.textAlign = 'center'
            c.textBaseline = 'middle'
            c.fillStyle = MUTED
            c.font = `500 11px ${FONT}`
            c.fillText('통합 매출', x, y - 12)
            c.fillStyle = INK
            c.font = `700 18px ${FONT}`
            c.fillText(self.formatCompact(self.combinedTotalAmt) + '원', x, y + 8)
            c.restore()
          }
        }]
      })
    },

    //3) 채널별 확정/취소 건수(누적 막대차트)
    renderStatusChart()
    {
      if(this.charts.status) this.charts.status.destroy()

      let ctx = this.$refs.statusChart.getContext('2d')
      this.charts.status = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['일반예약', '호텔예약', '외부결제'],
          datasets: [
            {
              label: '확정건수',
              data: [this.reservationSummary.totalCnt, this.hotelSummary.totalCnt, this.externalSummary.totalCnt],
              backgroundColor: '#334155',
              borderRadius: 6,
              maxBarThickness: 44,
            },
            {
              label: '취소건수',
              data: [this.reservationSummary.cxCnt, this.hotelSummary.cxCnt, this.externalSummary.cxCnt],
              backgroundColor: CX_COLOR,
              borderRadius: 6,
              maxBarThickness: 44,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { stacked: true, grid: { display: false } },
            y: {
              stacked: true,
              border: { display: false },
              grid: { color: GRID },
              ticks: { precision: 0, maxTicksLimit: 5 }
            }
          },
          plugins: {
            legend: LEGEND_BOTTOM,
            tooltip: {
              ...TOOLTIP_BASE,
              callbacks: {
                label: (c) => `${c.dataset.label}: ${this.getCurrencyFormat(c.raw)}건`
              }
            }
          }
        }
      })
    },

    //4) 채널별 취소율 비교(가로 막대차트)
    renderCxRateChart()
    {
      if(this.charts.cxRate) this.charts.cxRate.destroy()

      let ctx = this.$refs.cxRateChart.getContext('2d')
      this.charts.cxRate = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['일반예약', '호텔예약', '외부결제'],
          datasets: [{
            label: '취소율(%)',
            data: [
              Number(this.getCxRate(this.reservationSummary)),
              Number(this.getCxRate(this.hotelSummary)),
              Number(this.getCxRate(this.externalSummary)),
            ],
            backgroundColor: [CH_COLOR.reservation, CH_COLOR.hotel, CH_COLOR.external],
            borderRadius: 6,
            barThickness: 20,
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              min: 0,
              border: { display: false },
              grid: { color: GRID },
              ticks: { maxTicksLimit: 5, callback: (v) => v + '%' }
            },
            y: { grid: { display: false } }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...TOOLTIP_BASE,
              callbacks: {
                label: (c) => `취소율: ${c.raw}%`
              }
            }
          }
        }
      })
    },

    goToBack()
    {
      history.back();
    },
  }
}
</script>

<style scoped>
/* 기본 변수 및 페이지 배경 */
.eis-dashboard {
  --ink: #1B2434;
  --ink-soft: #4B5565;
  --muted: #8A93A3;
  --line: #E8EBF0;
  --bg: #F5F6F8;
  --gold: #B8935A;
  --alert: #E5484D;

  background: var(--bg);
  color: var(--ink);
  padding: 20px 14px 36px;
  font-family: 'Pretendard', 'Noto Sans KR', 'Malgun Gothic', sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* 헤더 */
.eis-header {
  margin-bottom: 16px;
}
.eis-eyebrow {
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 4px;
}
.eis-title {
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
}
.eis-subtitle {
  font-size: 0.82rem;
  color: var(--muted);
  margin: 0;
}

/* 조회기간 */
.filter-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.filter-dates {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  gap: 8px;
}
.date-field {
  display: flex;
  flex-direction: column;
  margin: 0;
  min-width: 0;
}
.date-field span {
  font-size: 0.68rem;
  color: var(--muted);
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}
.date-field input {
  height: 42px;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 10px;
  font-size: 16px;            /* iOS 입력창 자동확대 방지 */
  color: var(--ink);
  background: #fff;
  width: 100%;
  min-width: 0;
  outline: none;
}
.date-field input:focus {
  border-color: var(--ink);
}
.date-sep {
  color: var(--muted);
  padding-bottom: 10px;
}

.filter-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}
.range-pills {
  display: flex;
  gap: 8px;
}
.pill {
  flex: 1;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink-soft);
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s ease;
}
.pill.active {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}
.btn-search {
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: var(--ink);
  color: #fff;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.btn-search:active {
  opacity: 0.88;
}

.eis-note {
  font-size: 0.72rem;
  color: var(--muted);
  margin: 10px 2px 16px;
  line-height: 1.5;
}

/* 비교기간 버튼 */
.compare-pills .pill.active {
  background: var(--gold);
  border-color: var(--gold);
}

/* 대표지표(히어로) */
.hero-card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 22px 20px 18px;
  color: #fff;
  background: linear-gradient(150deg, #1B2434 0%, #243049 55%, #2F3E5C 100%);
  box-shadow: 0 8px 24px rgba(27, 36, 52, 0.18);
}
.hero-card::before {
  content: "";
  position: absolute;
  top: -80px;
  right: -60px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(184, 147, 90, 0.35) 0%, rgba(184, 147, 90, 0) 70%);
  pointer-events: none;
}
.hero-label {
  position: relative;
  font-size: 0.76rem;
  letter-spacing: 0.06em;
  color: #D8BE91;
  margin-bottom: 8px;
}
.hero-value {
  position: relative;
  font-size: clamp(1.7rem, 7.6vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.hero-sub {
  position: relative;
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 6px;
}
.hero-stats {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
}
.hero-stat {
  min-width: 0;
}
.hero-stat:not(:first-child) {
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  padding-left: 12px;
}
.hero-stat-label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
}
.hero-stat-value {
  font-size: 1.02rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.hero-stat-value.is-alert {
  color: #FF8A8F;
}
.hero-stat-sub {
  font-size: 0.66rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 비교기간 증감 표기 */
.hero-delta {
  position: relative;
  margin-top: 8px;
  font-size: 0.86rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.hero-delta span {
  margin-left: 8px;
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
}
.hero-stat-delta {
  margin-top: 2px;
  font-size: 0.72rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.channel-delta {
  margin-top: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.channel-delta span {
  margin-left: 6px;
  font-size: 0.7rem;
  font-weight: 400;
  color: var(--muted);
}

/* 증감 색상 : 어두운 배경(히어로)과 밝은 배경(채널카드) 분리 */
.hero-delta.tone-good, .hero-stat-delta.tone-good { color: #5FE0AA; }
.hero-delta.tone-bad,  .hero-stat-delta.tone-bad  { color: #FF8A8F; }
.hero-delta.tone-flat, .hero-delta.tone-na,
.hero-stat-delta.tone-flat, .hero-stat-delta.tone-na { color: rgba(255, 255, 255, 0.55); }

.channel-delta.tone-good { color: #0CA678; }
.channel-delta.tone-bad  { color: var(--alert); }
.channel-delta.tone-flat, .channel-delta.tone-na { color: var(--muted); }

/* 섹션 공통 */
.eis-section {
  margin-top: 26px;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin: 0 0 12px;
}
.section-title::before {
  content: "";
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: var(--gold);
  margin-right: 8px;
}

/* 채널 카드 : 모바일은 좌우 스와이프, 데스크톱은 3열 */
.channel-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  margin: 0 -14px;
  padding: 2px 14px 8px;
  scroll-padding: 0 14px;
  scrollbar-width: none;
}
.channel-scroll::-webkit-scrollbar {
  display: none;
}
.channel-card {
  flex: 0 0 80%;
  scroll-snap-align: start;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.04);
}
.channel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.channel-name {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 600;
}
.channel-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 7px;
}
.channel-share {
  font-size: 0.76rem;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.channel-amt {
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.channel-amt small {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--muted);
  margin-left: 2px;
}
.share-track {
  height: 5px;
  border-radius: 3px;
  background: #EEF0F3;
  margin: 12px 0 14px;
  overflow: hidden;
}
.share-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}
.channel-rows {
  border-top: 1px solid var(--line);
  padding-top: 8px;
}
.channel-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  padding: 4px 0;
  color: var(--ink-soft);
}
.channel-row b {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.channel-row b.is-alert {
  color: var(--alert);
}

/* 차트 카드 */
.chart-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 16px 14px 12px;
  height: 100%;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.04);
}
.chart-card-header {
  font-weight: 600;
  font-size: 0.88rem;
  margin: 0 4px 12px;
  color: var(--ink);
}
.chart-canvas-wrap {
  position: relative;
  height: 240px;
}

/* 상품 순위 */
.section-sub {
  margin-left: 8px;
  font-size: 0.7rem;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--muted);
}
.rank-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 16px 16px 6px;
  height: 100%;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.04);
}
.rank-card-header {
  font-weight: 600;
  font-size: 0.88rem;
  margin: 0 0 4px;
  color: var(--ink);
}
.rank-empty {
  padding: 18px 0 14px;
  font-size: 0.82rem;
  color: var(--muted);
  text-align: center;
}
.rank-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
}
.rank-row:not(:last-child) {
  border-bottom: 1px solid var(--line);
}
.rank-no {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #EEF0F3;
  color: var(--ink-soft);
  font-size: 0.76rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rank-no.is-top {
  background: var(--ink);
  color: #fff;
}
.rank-body {
  flex: 1;
  min-width: 0;
}
.rank-name {
  font-size: 0.86rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rank-track {
  height: 4px;
  border-radius: 2px;
  background: #EEF0F3;
  margin-top: 6px;
  overflow: hidden;
}
.rank-fill {
  height: 100%;
  border-radius: 2px;
  background: var(--gold);
  transition: width 0.5s ease;
}
.rank-figures {
  flex: none;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.rank-figures b {
  display: block;
  font-size: 0.86rem;
  font-weight: 700;
  white-space: nowrap;
}
.rank-figures span {
  font-size: 0.72rem;
  color: var(--muted);
  white-space: nowrap;
}

/* 하단 */
.eis-footer {
  margin-top: 24px;
}
.btn-back {
  width: 100%;
  height: 46px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  color: var(--ink-soft);
  font-size: 0.9rem;
  font-weight: 500;
}
.btn-back:active {
  background: #F1F3F5;
}

/* 태블릿 이상 */
@media (min-width: 768px) {
  .eis-dashboard {
    padding: 28px 32px 48px;
  }
  .eis-title {
    font-size: 1.7rem;
  }

  .filter-card {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    padding: 16px 20px;
  }
  .filter-dates {
    grid-template-columns: 160px auto 160px;
  }
  .date-field input {
    font-size: 0.9rem;
  }
  .filter-actions {
    flex-direction: row;
    align-items: center;
    margin-top: 0;
    gap: 12px;
  }
  .range-pills {
    gap: 8px;
  }
  .pill {
    flex: none;
    padding: 0 16px;
  }
  .btn-search {
    padding: 0 26px;
    border-radius: 10px;
  }

  .hero-card {
    padding: 30px 34px 24px;
  }
  .hero-value {
    font-size: 2.8rem;
  }
  .hero-stats {
    margin-top: 26px;
    padding-top: 20px;
  }
  .hero-stat:not(:first-child) {
    padding-left: 24px;
  }
  .hero-stat-value {
    font-size: 1.3rem;
  }
  .hero-stat-sub,
  .hero-stat-label {
    font-size: 0.76rem;
  }
  .hero-stat-delta {
    font-size: 0.8rem;
  }

  .channel-scroll {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    overflow: visible;
    margin: 0;
    padding: 0;
  }
  .channel-card {
    flex: none;
  }

  .chart-card {
    padding: 20px 20px 16px;
  }
  .chart-canvas-wrap {
    height: 300px;
  }

  .btn-back {
    width: auto;
    min-width: 140px;
    padding: 0 28px;
  }
}
</style>